import React, { useEffect, useState } from 'react'
import { Alert, Button, Card, Collapse, Flex, Select, Skeleton, Typography } from 'antd'
import { CheckCircleFilled, MailOutlined, SearchOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import { TimeAgo } from '../../components/time-ago'
import { useAuth } from '../../auth'
import { UPDATE_SITE } from '../../user/permission'
import { useChatSettings, useCheckInstallation, useSiteOverview, useUpdateChatSettings } from '../hooks'
import { InstallationCheck } from '../site'

type SiteProps = { siteId: string }

function Code({ children }: { children: string }) {
    return (
        <Typography.Paragraph
            copyable={{ text: children }}
            className="mb-0 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 font-mono text-xs break-all whitespace-pre-wrap"
        >
            {children}
        </Typography.Paragraph>
    )
}

/**
 * The snippet to paste, with short guides per platform and a check whether it is installed.
 */
export function SiteInstallation({ siteId }: SiteProps) {
    const { t } = useTranslation()
    const { data } = useSiteOverview(siteId)
    const check = useCheckInstallation(siteId)

    if (!data?.connection.snippet) {
        return <Skeleton active />
    }

    const hasVacancies = data.content.openVacancies > 0 || data.content.interviewAgents > 0

    const { snippet, csp = [], embedSeenAt } = data.connection
    const result = check.data
    const installed = result?.installed ?? embedSeenAt !== null

    return (
        <Card
            title={t('Install the snippet')}
            extra={
                installed && (
                    <Typography.Text type="success">
                        <CheckCircleFilled /> {t('Installed')}
                    </Typography.Text>
                )
            }
        >
            <Flex
                vertical
                gap="middle"
            >
                <Typography.Text type="secondary">
                    {t('Add this code to every page of your website, just before </body>. The chat then appears.')}
                    {hasVacancies &&
                        ` ${t('On vacancies with applying via WhatsApp, it also opens the apply form in a window on your website.')}`}
                </Typography.Text>
                <Code>{snippet}</Code>

                <InstallationGuides csp={csp} />

                <Flex
                    gap="small"
                    wrap
                    align="center"
                >
                    <Button
                        icon={<SearchOutlined />}
                        loading={check.isPending}
                        onClick={() => check.mutate()}
                    >
                        {t('Check installation')}
                    </Button>
                    <Button
                        icon={<MailOutlined />}
                        href={developerMail(t, snippet, csp)}
                    >
                        {t('Send to your developer')}
                    </Button>
                    {embedSeenAt && (
                        <Typography.Text type="secondary">
                            {t('Last loaded on the website')} <TimeAgo date={embedSeenAt} />
                        </Typography.Text>
                    )}
                </Flex>

                {result && <CheckResult result={result} />}
            </Flex>
        </Card>
    )
}

/**
 * A mail to the developer with the snippet and what it needs.
 */
function developerMail(t: TFunction, snippet: string, csp: string[]): string {
    const subject = t('Evaa chat for our website')
    const body = [
        t('Could you add this code to every page of our website, just before </body>?'),
        snippet,
        `${t('If the website uses a Content Security Policy, it also needs:')}\n${csp.join('\n')}`,
        t('It also works on single-page apps; the chat follows route changes by itself.'),
    ].join('\n\n')

    return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}

function Steps({ items }: { items: string[] }) {
    return (
        <ol className="mb-2 list-decimal pl-5">
            {items.map((item) => (
                <li key={item}>{item}</li>
            ))}
        </ol>
    )
}

function InstallationGuides({ csp }: { csp: string[] }) {
    const { t } = useTranslation()

    return (
        <Collapse
            size="small"
            items={[
                {
                    key: 'html',
                    label: t('Any website (HTML)'),
                    children: (
                        <Typography.Text>
                            {t(
                                'Paste the code in your template or layout, just before </body>, so it is on every page.'
                            )}
                        </Typography.Text>
                    ),
                },
                {
                    key: 'gtm',
                    label: 'Google Tag Manager',
                    children: (
                        <Steps
                            items={[
                                t('Add a new tag of the type "Custom HTML" and paste the code.'),
                                t('Choose the trigger "All Pages" (or "Initialization - All Pages").'),
                                t('Save the tag and publish the container.'),
                            ]}
                        />
                    ),
                },
                {
                    key: 'wordpress',
                    label: 'WordPress',
                    children: (
                        <Typography.Text>
                            {t(
                                'Use the Evaa plugin instead: Evaa then always knows what is on your website right now. You also see the chats in the plugin.'
                            )}
                        </Typography.Text>
                    ),
                },
                {
                    key: 'webflow',
                    label: 'Webflow',
                    children: (
                        <Steps
                            items={[
                                t('Open Site settings → Custom code.'),
                                t('Paste the code under "Footer code".'),
                                t('Save and publish the site.'),
                            ]}
                        />
                    ),
                },
                {
                    key: 'wix',
                    label: 'Wix',
                    children: (
                        <Steps
                            items={[
                                t('Open Settings → Custom code (needs a premium plan).'),
                                t('Add custom code, paste the code, choose "All pages" and "Body - end".'),
                                t('Apply.'),
                            ]}
                        />
                    ),
                },
                {
                    key: 'squarespace',
                    label: 'Squarespace',
                    children: (
                        <Steps
                            items={[
                                t('Open Settings → Advanced → Code injection.'),
                                t('Paste the code under "Footer" and save.'),
                            ]}
                        />
                    ),
                },
                {
                    key: 'shopify',
                    label: 'Shopify',
                    children: (
                        <Steps
                            items={[
                                t('Open Online Store → Themes → Edit code.'),
                                t('Open layout/theme.liquid and paste the code just before </body>.'),
                                t('Save.'),
                            ]}
                        />
                    ),
                },
                {
                    key: 'spa',
                    label: t('React, Vue, Next.js and other apps'),
                    children: (
                        <Typography.Text>
                            {t(
                                'Add the code to index.html or your root layout. The chat stays open while visitors move through the app, and apply buttons on new routes are picked up by themselves. Pages that are only built in the browser cannot be read by Evaa; render them on the server or as static pages.'
                            )}
                        </Typography.Text>
                    ),
                },
                {
                    key: 'csp',
                    label: t('For your developer: security settings'),
                    children: (
                        <Flex
                            vertical
                            gap="small"
                        >
                            <Typography.Text>
                                {t(
                                    'Only needed when your website uses a Content Security Policy. Your developer knows what this is and adds these lines:'
                                )}
                            </Typography.Text>
                            <Code>{csp.join('\n')}</Code>
                        </Flex>
                    ),
                },
            ]}
        />
    )
}

function CheckResult({ result }: { result: InstallationCheck }) {
    const { t } = useTranslation()

    if (result.installed) {
        const description =
            result.inHomepage === false
                ? t(
                      'It is not in the HTML of the homepage, but it loaded on the website recently, for example through a tag manager.'
                  )
                : undefined

        return (
            <Alert
                type="success"
                showIcon
                message={t('The snippet is installed.')}
                description={description}
            />
        )
    }

    const description = result.error
        ? t('The homepage could not be loaded: {{error}}', { error: result.error })
        : t(
              'It is not in the HTML of the homepage and has not loaded on the website. When you use a tag manager, open your website once and check again.'
          )

    return (
        <Alert
            type="warning"
            showIcon
            message={t('Evaa did not find the snippet yet.')}
            description={description}
        />
    )
}

/**
 * Other domains where the chat may run with this website's key, such as a staging website.
 */
export function SiteAllowedDomains({ siteId }: SiteProps) {
    const { t } = useTranslation()
    const { can } = useAuth()
    const { data } = useChatSettings(siteId)
    const update = useUpdateChatSettings(siteId)
    const [origins, setOrigins] = useState<string[]>([])

    useEffect(() => {
        if (data) {
            setOrigins(data.allowedOrigins)
        }
    }, [data])

    if (!data) {
        return null
    }

    const changed = JSON.stringify(origins) !== JSON.stringify(data.allowedOrigins)

    return (
        <Card title={t('Other domains')}>
            <Flex
                vertical
                gap="middle"
            >
                <Typography.Text type="secondary">
                    {t(
                        'The chat only works on this website. Add other websites where it may run too, for example a test website.'
                    )}
                </Typography.Text>
                <Select
                    mode="tags"
                    open={false}
                    suffixIcon={null}
                    tokenSeparators={[',', ' ']}
                    disabled={!can(UPDATE_SITE)}
                    value={origins}
                    onChange={(values: string[]) => setOrigins(values.map(withScheme))}
                    placeholder="https://staging.example.com"
                />
                {can(UPDATE_SITE) && (
                    <div>
                        <Button
                            type="primary"
                            disabled={!changed}
                            loading={update.isPending}
                            onClick={() => update.mutate({ allowedOrigins: origins })}
                        >
                            {t('Save')}
                        </Button>
                    </div>
                )}
            </Flex>
        </Card>
    )
}

function withScheme(origin: string): string {
    return /^https?:\/\//.test(origin) ? origin : `https://${origin}`
}
